import { useEffect, useState } from 'react'
import { IWalletCategory } from '~/@api/generated-api-types-user/Api'
import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import { cn } from '~/shadcn/lib/utils'
import { nonNullable } from '~/@lib/utils/type.utils'

import { StageLabel } from '~/@mid_ui/typography/StageLabel'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { Text } from '~/@mid_ui/typography/Text'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import {
  ProjectYieldText,
  type ProjectWithSchemAndExtraYieldSettings,
} from '~/@pages_investing/projects/_projects_ui/project-yield-text/ProjectYieldText'
import { HotBonusComponent } from '~/@pages_investing/projects/_projects_ui/hot-bonus/HotBonusComponent'
import { getProjectInvestmentPeriodString } from '~/@pages_investing/_utils/investmentPeriod.utils'
import { ProjectVipBadge } from '~/@pages_investing/projects/_projects_ui/project-vip-badge/ProjectVipBadge'
import { BoosterProjectTooltip } from '~/@pages_investing/projects/_projects_ui/booster-project/BoosterProjectTooltip'
import { useUser } from '~/@biz_account/useUser/useUser'
import { ProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeLabel'
import { ProjectPreviewImageAndPopup } from '~/@pages_investing/projects/_projects_ui/project-preview-and-label/ProjectPreviewImageAndPopup'
import { InvestButton } from '~/@pages_investing/projects/_projects_ui/invest-button/InvestButton'
import { ProjectImageCarousel } from '~/@pages_investing/projects/_projects_ui/project-image-carousel/ProjectImageCarousel'
import { getProjectCollateralSubcategoryLabel } from '~/@pages_investing/projects/_projects_ui/project-collateral/labelByCollateralSubcategory'
import { projectTooltipByType } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/projectTooltipByType'
import { DetailHeading } from '../project-detail_ui/detail-heading/DetailHeading'
import { BackButton } from '../project-detail_ui/back-button/BackButton'
import { HeadingOne } from '~/@mid_ui/typography/HeadingOne'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { FeeImmunityBadge } from '~/@pages_investing/fee-immunity/FeeImmunityBadge'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { formatFeeSaving } from '~/@pages_investing/fee-immunity/feeImmunityDisplay.utils'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'

import { useOrderOfferDataQuery } from '~/@pages_investing/order/order-form/useOrderOfferDataQuery'

type Props = {
  project: ProjectWithSchemAndExtraYieldSettings
  isInvestButtonDisabled: boolean
  onInvestClick(): void
}

export function RunningProjectV2Overview({
  project,
  isInvestButtonDisabled,
  onInvestClick,
}: Props) {
  const { user } = useUser()
  const { hasFeeImmunity } = useFeeImmunity()
  const { fee } = useRevenueFee()

  const feeImmunityBadgeValue = formatFeeSaving(
    project._calculatedValues.maxYieldValue > 0
      ? project._calculatedValues.maxYieldValue
      : project._calculatedValues.minYieldValue,
    fee
  )

  const feeImmunityBadge = hasFeeImmunity && (
    <FeeImmunityBadge
      value={feeImmunityBadgeValue}
      minYieldValue={project._calculatedValues.minYieldValue}
      maxYieldValue={project._calculatedValues.maxYieldValue}
      isCompoundingAnnually={
        project.yieldCompounding === IYieldCompounding.Annually
      }
    />
  )

  const investorWallet = user?.wallets.find(
    (wallet) => wallet.category === IWalletCategory.Investor
  )

  const amountTarget = project.amountTarget
  const amountCurrent = Math.min(project.amountTarget, project.amountCurrent)
  const amountPercentage = (amountCurrent * 100) / amountTarget
  const isDesktop = useIsDesktop()

  const { data: orderOfferData } = useOrderOfferDataQuery({
    businessId: project.businessId.toString(),
    stageNumber: project.stageNumber.toString(),
  })

  const [availableFutureRevenueFixed, setAvailableFutureRevenueFixed] =
    useState(orderOfferData?.availableFutureRevenueFixed || 0)

  const isVip = project.blurTags.length > 0

  useEffect(() => {
    const newAvailableFutureRevenueFixed =
      orderOfferData?.availableFutureRevenueFixed || 0

    setAvailableFutureRevenueFixed(newAvailableFutureRevenueFixed)
  }, [orderOfferData])

  return (
    <div className="w-full">
      <div
        className={
          'flex flex-col lg:flex-row w-full border-b border-border-strong bg-foreground h-auto'
        }>
        {isDesktop ? (
          <div className="flex flex-col items-start flex-1 gap-10 md:px-10 md:pt-5 md:pb-15">
            <BackButton>Späť</BackButton>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <ProjectVipBadge
                  blurTagNames={project.blurTagNames || []}
                  blurTags={project.blurTags || []}
                  createdUsingBlurTags={isVip}
                />
                <Text className="text-primary-foreground font-semibold">
                  Investičná príležitosť ID: {project.businessId}
                </Text>
              </div>
              <HeadingOne className="text-primary-foreground">
                Detail investície
              </HeadingOne>
              <div className="flex items-center gap-2.5">
                <ProjectPreviewImageAndPopup
                  project={project}
                  withPopup={false}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start flex-1 gap-5 p-5 pb-[30px]">
            <div className="flex items-center justify-between w-full">
              <BackButton className="text-primary-foreground" />
              <Text className="text-primary-foreground font-semibold">
                Investičná príležitosť ID: {project.businessId}
              </Text>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-[5px]">
                <ProjectVipBadge
                  blurTagNames={project.blurTagNames || []}
                  blurTags={project.blurTags || []}
                  createdUsingBlurTags={isVip}
                />
              </div>
            </div>
            <Text className="leading-[42px] text-[32px] text-primary-foreground font-semibold">
              Detail investície
            </Text>
            <div className="flex items-center gap-2.5">
              <ProjectPreviewImageAndPopup
                project={project}
                withPopup={false}
              />
            </div>
          </div>
        )}
        <div
          className={cn(
            'flex flex-col md:flex-row',
            'p-0 pb-5 px-5 lg:p-10 lg:pb-10 lg:pr-10 lg:pl-0',
            'items-baseline lg:items-center',
            'flex-[0.7]',
            'gap-10',
            'justify-between'
          )}>
          <div className="w-full flex flex-col justify-between">
            {project.stageNumber && (
              <>
                <div className="flex flex-row justify-between">
                  <Text className="uppercase w-[170px] font-semibold text-primary-foreground">
                    Ostáva investovať:
                  </Text>
                  <Text className="font-semibold text-primary-foreground">
                    <CurrencyFormatter
                      value={Math.max(
                        project.amountTarget - project.amountCurrent,
                        0
                      )}
                      className="text-2xl"
                      integerClassName="text-2xl"
                      decimalClassName="text-base"
                    />
                  </Text>
                </div>
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row items-center gap-1">
                    <Text className="mr-0.5 uppercasen font-semibold uppercase text-primary-foreground">
                      Veľkosť tranže
                    </Text>
                    <StageLabel>{project.stageNumber}</StageLabel>
                    <Text className="text-primary-foreground">:</Text>
                    <TextTooltip
                      iconClassName="w-4.5 h-4.5"
                      text="Celkový úverový rámec je rozdelený do viacerých tranží. Investori majú možnosť postupne investovať do jednotlivých tranží až do vyčerpania schválenej výšky úveru"
                    />
                  </div>
                  <Text className="text-primary-foreground font-semibold">
                    <CurrencyFormatter
                      value={project.amountTarget}
                      className="text-base"
                      integerClassName="text-base"
                      decimalClassName="text-sm"
                    />
                  </Text>
                </div>
                {/* Progress Bar */}
                <div className="my-4">
                  <div className="w-full h-4 bg-border-strong rounded-[3px] overflow-hidden relative">
                    <div
                      className="h-full bg-accent rounded-[3px] transition-[width] duration-300 ease-in-out"
                      style={{ width: `${amountPercentage}%` }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <InvestButton
            disabled={isInvestButtonDisabled}
            className={cn(
              'w-1/2 md:w-fit',
              'self-center',
              'z-40',
              'fixed bottom-5 md:relative md:bottom-auto'
            )}
            onInvest={onInvestClick}
          />
        </div>
      </div>
      {!isDesktop && (
        <ProjectImageCarousel
          projectId={project._id}
          usage={'Details'}
          images={project.images}
        />
      )}
      <div
        className={cn(
          'bg-foreground',
          'flex flex-col lg:flex-row',
          '[&>div]:flex-1',
          '[&>div]:h-[120px]',
          '[&>div:not(:first-child)]:lg:border-l [&>div]:border-b [&>div]:border-border-strong',
          '[&>div]:p-5 [&>div]:lg:p-3 [&>div]:lg:px-6 [&>div]:lg:pb-4',
          '[&>div]:flex',
          '[&>div]:flex-row [&>div]:lg:flex-col',
          '[&>div]:justify-between'
        )}>
        {[
          {
            title: (
              <div className="flex justify-between items-center gap-2">
                <span>Úrok ako predpokladaný výnos:</span>
                <span className="lg:block hidden">{feeImmunityBadge}</span>
              </div>
            ),
            content: (
              <div className="flex flex-col lg:items-start items-end gap-2">
                <div className="flex flex-row items-center gap-2.5">
                  <Text className="flex flex-row items-center gap-1 text-md md:text-2xl font-semibold text-green">
                    <ProjectYieldText
                      _calculatedValues={project._calculatedValues}
                      isYieldCompoundingAnnually={
                        project.yieldCompounding === IYieldCompounding.Annually
                      }
                      yieldIconSize={isDesktop ? 36 : 24}
                      className="justify-start"
                    />
                  </Text>
                  {project.investmentFutureRevenueEnabled && investorWallet && (
                    <BoosterProjectTooltip
                      investorWallet={investorWallet}
                      availableFutureRevenueFixed={availableFutureRevenueFixed}
                    />
                  )}
                </div>
                <HotBonusComponent {...project.hotBonus} />
                <span className="block lg:hidden">{feeImmunityBadge}</span>
              </div>
            ),
          },
          {
            title: (
              <DetailHeading
                title="Doba úročenia:"
                tooltip={projectTooltipByType.investmentPeriod}
              />
            ),
            content: (
              <Text className="flex items-center justify-start gap-2 uppercase text-primary-foreground font-semibold text-md md:text-2xl">
                {getProjectInvestmentPeriodString(
                  project.investmentPeriod,
                  project.yieldCompounding
                )}{' '}
                mesiacov
              </Text>
            ),
          },
          {
            title: (
              <DetailHeading
                title="Typ výnosu:"
                tooltip={projectTooltipByType.paymentType}
              />
            ),
            content: (
              <div className="flex items-center justify-start gap-2">
                <Text className="text-primary-foreground text-md md:text-2xl font-semibold whitespace-nowrap">
                  <ProjectPaymentTypeLabel
                    projectPaymentType={project.paymentTypeId}
                    isYieldCompounding={
                      project.yieldCompounding === IYieldCompounding.Annually
                    }
                  />
                </Text>
              </div>
            ),
          },
          {
            title: 'Typ zaistenia:',
            content: (
              <div className="flex items-center justify-start gap-2">
                <Text className="flex flex-1 text-primary-foreground text-md md:text-2xl font-semibold">
                  {getProjectCollateralSubcategoryLabel(
                    project.collateralInfo.collateralSubcategory
                  )}
                </Text>
              </div>
            ),
          },
        ]
          .filter(nonNullable)
          .map(({ title, content }, index) => (
            <div
              key={`Overview-Detail-${index}`}
              className="flex flex-col pr-3">
              <DetailHeading title={title} />
              {content}
            </div>
          ))}
      </div>
      {isDesktop && (
        <ProjectImageCarousel
          projectId={project._id}
          usage={'Details'}
          images={project.images}
        />
      )}
    </div>
  )
}
